/**
 * One title's support workspace (/support/:pub): the queue, the selected conversation
 * (with notes, saved replies and the Reply panel) and the member panel.
 * `?c=contact-41` selects a conversation, even one outside the current queue page.
 */
import { useCallback, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import { TITLE_LABELS, type DeskChannel, type DeskConversation, type DeskState, type DeskSummary } from '../../lib/supportDesk'
import { conversationParam, defaultSort, isDeskBucket, parseConversationParam, type DeskSort } from '../../lib/supportWorkspace'
import { ConversationView } from '../../components/support/ConversationView'
import { MemberPanel } from '../../components/support/MemberPanel'
import { QUEUE_PAGE, QueueList } from '../../components/support/QueueList'
import { TopicFilter } from '../../components/support/TopicFilter'
import { deskOff, useDeskMe } from './SupportQueue'

const TABS: { key: DeskState; label: string }[] = [
  { key: 'to_review', label: 'To review' }, { key: 'waiting', label: 'Waiting on customer' },
  { key: 'snoozed', label: 'Snoozed' }, { key: 'done', label: 'Done' },
]
const CHANNELS: { key: '' | DeskChannel; label: string }[] = [
  { key: '', label: 'All channels' }, { key: 'contact', label: 'Contact form' },
  { key: 'feedback', label: 'Feedback' }, { key: 'email', label: 'Email' },
]

export function SupportTitle() {
  const { pub } = useParams()
  if (!isDeskBucket(pub)) {
    return (
      <div className="p-8 max-w-[640px]">
        <div className="label mb-2">Customer Support</div>
        <p className="font-serif text-2xl text-paper">No such title</p>
        <Link to="/support" className="label-amber hover:underline">Back to Customer Support</Link>
      </div>
    )
  }
  return <Workspace key={pub} pub={pub} />
}

function Workspace({ pub }: { pub: string }) {
  const [search, setSearch] = useSearchParams()
  const selected = parseConversationParam(search.get('c'))
  const selectedKey = selected ? conversationParam(selected.channel, selected.id) : null
  const { staff, canAct, meEmail } = useDeskMe()
  const [tab, setTab] = useState<DeskState>('to_review')
  const [channel, setChannel] = useState<'' | DeskChannel>('')
  const [topic, setTopic] = useState('')
  const [sort, setSort] = useState<DeskSort>('oldest')
  const [offset, setOffset] = useState(0)

  const summary = useQuery<DeskSummary>({
    queryKey: ['support', 'summary'], queryFn: () => api.get<DeskSummary>('/api/support/summary'),
    refetchInterval: 60_000, retry: false,
  })

  const params = new URLSearchParams({ state: tab, publication: pub, sort, limit: String(QUEUE_PAGE), offset: String(offset) })
  if (channel) params.set('channel', channel)
  if (topic) params.set('topic', topic)
  // The topic chips count under every other filter, so each count is what clicking it lists.
  const topicFilters = new URLSearchParams({ state: tab, publication: pub })
  if (channel) topicFilters.set('channel', channel)

  const select = useCallback((c: { channel: DeskChannel; message_id: number }, replace = false) => {
    setSearch(prev => {
      const next = new URLSearchParams(prev)
      next.set('c', conversationParam(c.channel, c.message_id))
      return next
    }, { replace })
  }, [setSearch])
  // Nothing chosen yet: open the top of the queue (without adding a history entry).
  const hasSelection = selectedKey !== null
  const onFirst = useCallback((c: DeskConversation | undefined) => {
    if (c && !hasSelection) select(c, true)
  }, [hasSelection, select])

  if (deskOff(summary.error)) {
    return (
      <div className="p-8 max-w-[640px]">
        <div className="label mb-2">Customer Support</div>
        <p className="font-serif text-2xl text-paper">Customer Support desk is not switched on</p>
        <p className="mt-2 text-sm text-paper-dim">Ask an administrator to turn it on. The existing inboxes keep working in the meantime.</p>
      </div>
    )
  }

  const toReview = summary.data?.titles?.[pub as keyof DeskSummary['titles']]?.to_review
  const reset = () => setOffset(0)

  return (
    <div className="flex flex-col min-h-full">
      <header className="flex flex-wrap items-end gap-4 px-6 py-4 border-b border-ink-500">
        <div>
          <Link to="/support" className="label hover:underline">Customer Support</Link>
          <h1 className="font-serif text-[30px] leading-tight text-paper">
            {TITLE_LABELS[pub]} <span className="text-paper-dim text-xl">· {toReview ?? '–'} to review</span>
          </h1>
        </div>
        <div role="tablist" className="flex flex-wrap gap-1 ml-auto">
          {TABS.map(t => (
            <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => { setTab(t.key); setSort(defaultSort(t.key)); reset() }}
              className={`font-mono text-[11px] uppercase tracking-tight-cap px-2 py-1 border rounded-sm ${tab === t.key ? 'border-amber-glow text-amber-glow' : 'border-ink-500 text-paper-dim'}`}>
              {t.label}{t.key === 'to_review' && toReview != null ? ` ${toReview}` : ''}
            </button>
          ))}
        </div>
        <label className="sr-only" htmlFor="channel-filter">Channel</label>
        <select id="channel-filter" value={channel} onChange={e => { setChannel(e.target.value as '' | DeskChannel); reset() }}
          className="bg-ink-900 border border-ink-500 px-2 py-2 text-paper font-mono text-[12px]">
          {CHANNELS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <div className="basis-full">
          <TopicFilter filters={topicFilters} value={topic} onChange={t => { setTopic(t); reset() }} />
        </div>
      </header>

      <div className="grid grid-cols-1 xl:grid-cols-[300px_minmax(0,1fr)_300px] flex-1 min-h-0">
        <QueueList params={params} sort={sort} onSort={s => { setSort(s); reset() }} offset={offset} onOffset={setOffset}
          selected={selectedKey} onSelect={c => select(c)} onFirst={onFirst} />
        {selected ? (
          <>
            <ConversationView key={selectedKey} channel={selected.channel} id={selected.id} publication={pub}
              canAct={canAct} staff={staff} meEmail={meEmail} />
            <MemberPanel key={`m-${selectedKey}`} channel={selected.channel} id={selected.id} />
          </>
        ) : (
          <p className="p-6 text-paper-trace">Choose a conversation from the queue.</p>
        )}
      </div>
    </div>
  )
}
